/* eslint-disable camelcase */
import React from 'react';
import { Card, Badge, Row, Col } from 'react-bootstrap';
import { KanbanCard } from '../../../DesignSystems/Kanban';
import { StylishNewButton } from '../../../DesignSystems/New/StylishNewButton';
import { FaPhone, FaUsers, FaUser, FaMapMarkerAlt } from 'react-icons/fa';
import classNames from 'classnames';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useWorkAssignments } from '../../hooks/workAssignmentHooks';
import { ResourceInIncident } from '../../hooks/resourceInIncidentHooks';
import { useResourceSets } from '../../hooks/resourceSetHooks';
import { getLatLongFromGeometry } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/getLatLongFromGeometry';
import { useResourceSites } from '../../hooks/resourceSiteHooks';
import { STATUS_CONFIG, SOURCE_CONFIG } from '../constants/resourceConstants';
import WorkAssignmentSection from './WorkAssignmentSection';
import { useCurrentUserApprovalAuthority } from '../../../../hooks/incident/useCurrentUserApprovalAuthority';

dayjs.extend(relativeTime);

interface ResourceCardProps {
  card: KanbanCard;
  onCheckIn?: (resource: ResourceInIncident) => void;
  onUpdateCheckIn?: (resource: ResourceInIncident) => void;
  onStatusUpdate?: (resource: ResourceInIncident) => void;
  onCheckOut?: (resource: ResourceInIncident) => void;
  onUpdateCheckOut?: (resource: ResourceInIncident) => void;
  onCancel?: (resource: ResourceInIncident) => void;
  onExportICS221?: (resource: ResourceInIncident) => void;
}

const ResourceCard: React.FC<ResourceCardProps> = ({
  card,
  onCheckIn,
  onUpdateCheckIn,
  onStatusUpdate,
  onCheckOut,
  onUpdateCheckOut,
  onCancel,
  onExportICS221,
}) => {
  const { data: workAssignments = [] } = useWorkAssignments();
  const resourceSets = useResourceSets();
  const { data: resourceSites = [] } = useResourceSites();
  const hasApprovalAuthority = useCurrentUserApprovalAuthority();
  const resource = card.metadata as ResourceInIncident;
  const status = resource.current_status || 'in_transit';
  const statusConfig =
    STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] ||
    STATUS_CONFIG.available;
  const StatusIcon = statusConfig.icon;

  const sourceType =
    (resource as ResourceInIncident & { source_type?: string }).source_type ||
    'OWNED';
  const sourceConfig = SOURCE_CONFIG[sourceType as keyof typeof SOURCE_CONFIG];

  // Find the corresponding org resource for location data
  const orgResource = resourceSets.data?.find(
    (rs) => rs.resource_set_id === resource.resource_set_id
  );

  // Check if org resource has any location information
  const hasLocationInfo =
    orgResource &&
    (orgResource.source_location ||
      orgResource.source_resource_site_id ||
      orgResource.geometry ||
      orgResource.resource_site_id ||
      orgResource.destination_location ||
      orgResource.destination_resource_site_id);

  // Helper function to format location display
  const formatLocation = (
    location: {
      address?: string;
      name?: string;
      lat?: number;
      lng?: number;
      latitude?: number;
      longitude?: number;
    } | null,
    siteId?: string
  ) => {
    if (!location && !siteId) return null;

    // If we have a site ID, try to find the site name
    if (siteId && resourceSites) {
      const site = resourceSites.find((s) => s.id === siteId);
      if (site) return site.name;
    }

    // If we have location object, check for name first
    if (location) {
      // Check if location has a name field (from geolocation JSON)
      if (location.name) {
        return location.name;
      }
      // Fall back to coordinates if available
      const latitude = location.latitude ?? location.lat;
      const longitude = location.longitude ?? location.lng;
      if (latitude != null || longitude != null) {
        return `${latitude?.toFixed(4)}, ${longitude?.toFixed(4)}`;
      }
    }

    return null;
  };

  return (
    <Card
      className="mb-2 border bg-white"
      style={{
        cursor: 'pointer',
        borderLeft: `4px solid ${statusConfig.bgColor}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
      }}
    >
      <Card.Body className="p-3">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="text-dark mb-1">
              {resource.resource_name || 'Unknown Resource'}
            </h6>
            <div className="d-flex align-items-center gap-2">
              {resource.order_request_number && (
                <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
                  {resource.order_request_number}
                </Badge>
              )}
              {sourceConfig && (
                <Badge
                  bg={sourceConfig.color}
                  className="text-uppercase"
                  style={{ fontSize: '0.7rem' }}
                >
                  {sourceConfig.label}
                </Badge>
              )}
            </div>
          </div>
          <Badge
            bg={statusConfig.color}
            className="d-flex align-items-center gap-1"
          >
            <StatusIcon size={12} />
            {statusConfig.label}
          </Badge>
        </div>

        {/* Resource Kind and Quantity */}
        <div className="text-muted small mb-2">
          <strong>
            {resource.resource_set_kind ||
              resource.resource_type ||
              resource.resource_kind ||
              'Unknown Kind'}
          </strong>{' '}
          • Qty:{' '}
          <strong>
            {resource.resource_set_quantity || resource.personnel_count || 1}
          </strong>
          {(resource as ResourceInIncident & { resource_subtype?: string })
            .resource_subtype && (
            <>
              {' '}
              •{' '}
              {
                (resource as ResourceInIncident & { resource_subtype?: string })
                  .resource_subtype
              }
            </>
          )}
        </div>

        {/* Personnel Info */}
        {resource.leader_name && (
          <div className="bg-light mb-2 rounded p-2">
            <div className="d-flex align-items-center gap-2">
              <FaUser size={12} className="text-primary" />
              <span className="small text-dark">
                <strong>Leader:</strong> {resource.leader_name}
              </span>
              {resource.personnel_count && (
                <>
                  <FaUsers size={12} className="text-primary ms-2" />
                  <span className="small text-dark">
                    <strong>{resource.personnel_count}</strong> personnel
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Work Assignment Information */}
        <WorkAssignmentSection
          resource={resource}
          workAssignments={workAssignments}
        />

        {/* Org Resource Location Info - Show when available */}
        {hasLocationInfo && (
          <div className="bg-light mb-2 rounded border p-2">
            <div className="small">
              <strong className="text-primary d-block mb-2">
                <FaMapMarkerAlt size={12} className="me-1" />
                Location
              </strong>
              {/* Source Location */}
              {(orgResource.source_location ||
                orgResource.source_resource_site_id) && (
                <div className="mb-1">
                  <strong className="text-muted">Source:</strong>{' '}
                  <span className="text-dark">
                    {formatLocation(
                      orgResource.source_location,
                      orgResource.source_resource_site_id
                    ) || '-'}
                  </span>
                </div>
              )}
              {/* Current Location */}
              {(orgResource.geometry || orgResource.resource_site_id) && (
                <div className="mb-1">
                  <strong className="text-muted">Current:</strong>{' '}
                  <span className="text-dark">
                    {formatLocation(
                      getLatLongFromGeometry(orgResource.geometry),
                      orgResource.resource_site_id
                    ) || '-'}
                  </span>
                </div>
              )}
              {/* Destination Location */}
              {(orgResource.destination_location ||
                orgResource.destination_resource_site_id) && (
                <div className="mb-1">
                  <strong className="text-muted">Destination:</strong>{' '}
                  <span className="text-dark">
                    {formatLocation(
                      orgResource.destination_location,
                      orgResource.destination_resource_site_id
                    ) || '-'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact and Assignment Location Info */}
        {(resource.incident_contact_info ||
          resource.incident_assignment_location) && (
          <div className="mb-2">
            {resource.incident_contact_info && (
              <div className="d-flex align-items-start gap-2">
                <FaPhone size={12} className="text-success mt-1" />
                <span className="small text-dark">
                  <strong>Contact:</strong> {resource.incident_contact_info}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Timing Information */}
        {(resource.estimated_return_time ||
          (status === 'in_transit' && resource.checkin_datetime)) && (
          <div className="bg-light mt-2 rounded border p-2">
            <Row className="g-2">
              {/* Assignment Time for in-transit only */}
              {status === 'in_transit' && resource.checkin_datetime && (
                <Col xs={12}>
                  <div className="small">
                    <strong className="text-muted">
                      Assigned to Incident:
                    </strong>{' '}
                    <span className="text-dark">
                      {dayjs(resource.checkin_datetime).format(
                        'MM/DD/YY HH:mm'
                      )}
                      ({dayjs(resource.checkin_datetime).fromNow()})
                    </span>
                  </div>
                </Col>
              )}

              {/* ETR */}
              {resource.estimated_return_time && (
                <Col xs={12}>
                  <div className="small">
                    <strong className="text-warning">ETR:</strong>{' '}
                    <span className="text-dark">
                      {dayjs(resource.estimated_return_time).format(
                        'MM/DD/YY HH:mm'
                      )}
                      (in {dayjs(resource.estimated_return_time).fromNow(true)})
                    </span>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        )}

        {/* Notes - moved above action buttons */}
        {resource.status_notes && (
          <div className="border-start border-warning bg-light mt-2 border-3 p-2">
            <small className="text-muted d-block mb-1">
              <strong>Notes:</strong>
            </small>
            <small className="text-dark">{resource.status_notes}</small>
          </div>
        )}

        {/* Action Buttons */}
        <div className="d-flex justify-content-end mt-3 flex-wrap gap-2">
          {status === 'in_transit' && (
            <>
              {onCancel && (
                <StylishNewButton
                  className={classNames('button', {
                    'button--secondary': !hasApprovalAuthority,
                    'button--link text-danger': hasApprovalAuthority,
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel(resource);
                  }}
                  disabled={!hasApprovalAuthority}
                  title={
                    !hasApprovalAuthority
                      ? "You don't have approval authority to cancel assignments"
                      : ''
                  }
                >
                  Cancel Assignment
                </StylishNewButton>
              )}
              {onCheckIn && (
                <StylishNewButton
                  className={classNames('button', {
                    'button--secondary': !hasApprovalAuthority,
                    'button--primary': hasApprovalAuthority,
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCheckIn(resource);
                  }}
                  disabled={!hasApprovalAuthority}
                  title={
                    !hasApprovalAuthority
                      ? "You don't have approval authority to check in resources"
                      : ''
                  }
                >
                  Check In
                </StylishNewButton>
              )}
            </>
          )}

          {[
            'available',
            'assigned',
            'os_rest',
            'os_personnel',
            'os_mechanical',
            'etr',
          ].includes(status) && (
            <>
              {onStatusUpdate && (
                <StylishNewButton
                  className="button button--primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusUpdate(resource);
                  }}
                >
                  Update Status
                </StylishNewButton>
              )}
              {onUpdateCheckIn && (
                <StylishNewButton
                  className={classNames('button', {
                    'button--secondary': !hasApprovalAuthority,
                    'button--danger button--outline': hasApprovalAuthority,
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    onUpdateCheckIn(resource);
                  }}
                  disabled={!hasApprovalAuthority}
                  title={
                    !hasApprovalAuthority
                      ? "You don't have approval authority to update check-ins"
                      : ''
                  }
                >
                  Update Check-In
                </StylishNewButton>
              )}
            </>
          )}

          {[
            'available',
            'assigned',
            'os_rest',
            'os_personnel',
            'os_mechanical',
            'etr',
          ].includes(status) &&
            onCheckOut && (
              <StylishNewButton
                className="button button--danger button--outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onCheckOut(resource);
                }}
              >
                Check Out
              </StylishNewButton>
            )}

          {(status === 'checked_out' || status === 'in_process') &&
            onUpdateCheckOut && (
              <StylishNewButton
                className="button button--primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateCheckOut(resource);
                }}
              >
                Update Check-Out
              </StylishNewButton>
            )}

          {(status === 'checked_out' || status === 'in_process') &&
            onExportICS221 && (
              <StylishNewButton
                className="btn btn-outline-primary btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onExportICS221(resource);
                }}
                title="Preview individual resource demobilization check-out form"
              >
                Preview ICS 221
              </StylishNewButton>
            )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default ResourceCard;
